import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import React from 'react';
import { formatRate } from 'src/utils';
import {
  IRateScaleDerivation,
  IRateScaleNotAppliedDerivation,
  IRateScaleOvertimeDerivation,
} from 'src/app/timesheets/types';

// Kept as a plain function outside JSX so `.toFixed` isn't flagged by the
// lib/-tier no-toFixed-in-JSX lint rule; mirrors formatStepValue in RateFormula.
const formatLevelModifier = (percent: number): string =>
  `${percent.toFixed(2)}%`;

export interface ILevelModifierDerivation {
  scaleBaseRate?: number;
  effectiveScaleBaseRate?: number;
  scaleFringeRate?: number;
  effectiveScaleFringeRate?: number;
  levelRateModifierPercent?: number;
}

const LevelModifierNote: React.FC<{ derivation: ILevelModifierDerivation }> = ({
  derivation,
}) => {
  if (derivation.levelRateModifierPercent === undefined) return null;
  return (
    <>
      <Text size="xs" c="dimmed">
        Scale Base ({formatRate(derivation.scaleBaseRate)}) × Level (
        {formatLevelModifier(derivation.levelRateModifierPercent)}) ={' '}
        {formatRate(derivation.effectiveScaleBaseRate)}
      </Text>
      <Text size="xs" c="dimmed">
        Scale Fringe ({formatRate(derivation.scaleFringeRate)}) × Level (
        {formatLevelModifier(derivation.levelRateModifierPercent)}) ={' '}
        {formatRate(derivation.effectiveScaleFringeRate)}
      </Text>
    </>
  );
};

export interface IScaleOvertimeDerivationNoteProps {
  derivation: IRateScaleOvertimeDerivation;
}

/**
 * Derivation lines for the ScaleOvertime scenario. Explains how the effective scale
 * inputs are computed (via the level modifier, if any) and how the fringe delta falls
 * out of (scale fringe − worker benefits). BaseBenefitRate is nullable on rows
 * reconstructed from persisted fields; in that case we show fringeDelta alone.
 */
export const ScaleOvertimeDerivationNote: React.FC<
  IScaleOvertimeDerivationNoteProps
> = ({ derivation }) => (
  <Stack gap={2} mb="xs">
    <LevelModifierNote derivation={derivation} />
    {derivation.baseBenefitRate !== undefined &&
    derivation.baseBenefitRate !== null ? (
      <Text size="xs" c="dimmed">
        Fringe Delta: max(Effective Scale Fringe (
        {formatRate(derivation.effectiveScaleFringeRate)}) − Worker Benefits (
        {formatRate(derivation.baseBenefitRate)}), 0) ={' '}
        {formatRate(derivation.fringeDelta)}
      </Text>
    ) : (
      <Text size="xs" c="dimmed">
        Fringe Delta: {formatRate(derivation.fringeDelta)}
      </Text>
    )}
  </Stack>
);

export interface IScaleDerivationNoteProps {
  derivation: IRateScaleDerivation;
}

export const ScaleDerivationNote: React.FC<IScaleDerivationNoteProps> = ({
  derivation,
}) => (
  <Stack gap={2} mb="xs">
    <LevelModifierNote derivation={derivation} />
    <Text size="xs" c="dimmed">
      Actual Total: Base ({formatRate(derivation.baseRate)}) + Fringe Benefits (
      {formatRate(derivation.baseBenefitRate)}) ={' '}
      {formatRate(derivation.actualTotalComp)}
    </Text>
    <Text size="xs" c="dimmed">
      Scale Total: Effective Scale Base (
      {formatRate(derivation.effectiveScaleBaseRate)}) + Effective Scale Fringe
      ({formatRate(derivation.effectiveScaleFringeRate)}) ={' '}
      {formatRate(derivation.scaleTotalComp)}
    </Text>
    <Text size="xs" c="dimmed">
      Scale Adjustment: Scale Total ({formatRate(derivation.scaleTotalComp)}) −
      Actual Total ({formatRate(derivation.actualTotalComp)}) ={' '}
      {formatRate(derivation.scalePayAdjustmentAmount)}
    </Text>
  </Stack>
);

export interface IScaleNotAppliedDerivationNoteProps {
  derivation: IRateScaleNotAppliedDerivation;
}

export const ScaleNotAppliedDerivationNote: React.FC<
  IScaleNotAppliedDerivationNoteProps
> = ({ derivation }) => {
  if (derivation.jobClassificationLevelRateFound === false) {
    const userClass = derivation.userClassificationName;
    const jobClasses = derivation.jobConfiguredClassificationNames;
    return (
      <Stack gap={2} mb="xs">
        <Text size="xs" c="dimmed">
          No scale rate matched this user's classification on this job. The
          lookup is a join between the user's classification and the
          classifications the job has rates for — check both sides.
        </Text>
        {userClass !== undefined && (
          <Text size="xs" c="dimmed">
            User's classification:{' '}
            <Text component="span" fw={600}>
              {userClass ?? '(none assigned)'}
            </Text>
          </Text>
        )}
        {jobClasses !== undefined && jobClasses !== null && (
          <Text size="xs" c="dimmed">
            Job has rates for:{' '}
            <Text component="span" fw={600}>
              {jobClasses.length > 0 ? jobClasses.join(', ') : '(none)'}
            </Text>
          </Text>
        )}
      </Stack>
    );
  }

  return (
    <Stack gap={2} mb="xs">
      <LevelModifierNote derivation={derivation} />
      {derivation.baseBenefitRate !== undefined &&
      derivation.baseBenefitRate !== null ? (
        <Text size="xs" c="dimmed">
          Your Base ({formatRate(derivation.baseRate)}) + Fringe Benefits (
          {formatRate(derivation.baseBenefitRate)}) already meets or exceeds the
          scale total.
        </Text>
      ) : (
        <Text size="xs" c="dimmed">
          Your base rate ({formatRate(derivation.baseRate)}) already meets or
          exceeds the scale total.
        </Text>
      )}
    </Stack>
  );
};
